import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { api, ApiError } from '../api/client';
import './MasterInboxForwarding.css';

type RunStatus = 'blocked' | 'reconciled' | 'applying' | 'completed' | 'failed';

/** A terminal run that finished with errored panels is surfaced RED (not the
 *  green 'completed'), so a partly-failed run stands out in the run history. */
function runStatusView(status: RunStatus, errored: number): { label: string; cls: string } {
  if ((status === 'completed' || status === 'failed') && errored > 0) {
    return { label: `${status} · ${errored} errored`, cls: 'error' };
  }
  return { label: status, cls: status };
}
type PanelSource = 'panel' | 'inventory_serken' | 'inventory_regular';
type ReconcileStatus = 'pending' | 'reachable' | 'unreachable';
type ApplyStatus = 'pending' | 'running' | 'applied' | 'conflict' | 'error' | 'skipped';
type MatchStatus = 'pending' | 'matched' | 'unmatched';
type ApplyResult = 'pending' | 'added' | 'already' | 'conflict';

interface RunInfo {
  id: string;
  rule_name: string | null;
  route_to_original: boolean;
  status: RunStatus;
  panels_total: number;
  panels_done: number;
  panels_errored: number;
  auto_retry_enabled: boolean;
  auto_retry_attempts_used: number;
  auto_retry_attempts_cap: number;
  triggered_by: string | null;
  created_at: string;
  reconciled_at: string | null;
  applied_at: string | null;
  finished_at: string | null;
}

interface PanelRow {
  panel_email: string;
  source: PanelSource;
  reconcile_status: ReconcileStatus;
  user_count: number;
  apply_status: ApplyStatus;
  added_count: number;
  already_count: number;
  conflict_count: number;
  error_code: string | null;
  error_message: string | null;
}

interface EntryRow {
  source_email: string;
  master_email: string;
  target_panel_email: string;
  match_status: MatchStatus;
  unmatched_reason: string | null;
  apply_result: ApplyResult;
  existing_master: string | null;
  conflict_resolution: 'override' | 'skip' | null;
  verified: boolean;
}

// Mirror of src/lib/mapping-list.ts MappingValidationError.
type ValidationError =
  | { code: 'self_map'; source: string }
  | { code: 'source_is_also_master'; address: string }
  | { code: 'duplicate_source'; source: string; masters: string[] };

interface CreateResp {
  run_id: string | null;
  status: RunStatus;
  invalid: string[];
  validation_errors?: ValidationError[];
}

interface Detail {
  run: RunInfo;
  panels: PanelRow[];
  entries: EntryRow[];
}

interface Toast {
  type: 'success' | 'error' | 'info';
  message: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SOURCE_LABEL: Record<PanelSource, string> = {
  panel: 'Panel',
  inventory_serken: 'Serken',
  inventory_regular: 'Regular',
};

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── Client-side mirror of src/lib/mapping-list.ts ──────────────────────
// Reproduced verbatim so the operator sees loop violations before the
// server re-validates authoritatively on reconcile.

interface MappingEntry {
  source: string;
  master: string;
}

interface MappingAnalysis {
  entries: MappingEntry[];
  invalid: string[];
  errors: ValidationError[];
}

function parseMapping(raw: string): { entries: MappingEntry[]; invalid: string[] } {
  const seen = new Set<string>();
  const entries: MappingEntry[] = [];
  const invalid: string[] = [];
  for (const line of raw.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (trimmed === '') continue;
    const parts = trimmed.split(',').map((p) => p.trim());
    if (parts.length !== 2) {
      invalid.push(trimmed);
      continue;
    }
    const source = (parts[0] ?? '').toLowerCase();
    const master = (parts[1] ?? '').toLowerCase();
    if (!EMAIL_RE.test(source) || !EMAIL_RE.test(master)) {
      invalid.push(trimmed);
      continue;
    }
    const key = `${source},${master}`;
    if (seen.has(key)) continue;
    seen.add(key);
    entries.push({ source, master });
  }
  return { entries, invalid };
}

function validateMapping(entries: MappingEntry[]): ValidationError[] {
  const errors: ValidationError[] = [];

  // self-map A -> A
  for (const e of entries) {
    if (e.source === e.master) errors.push({ code: 'self_map', source: e.source });
  }

  // chain / ping-pong: an address is both a source and a master (excluding
  // self-maps, already reported above).
  const nonSelf = entries.filter((e) => e.source !== e.master);
  const sources = new Set(nonSelf.map((e) => e.source));
  const masters = new Set(nonSelf.map((e) => e.master));
  for (const addr of sources) {
    if (masters.has(addr)) errors.push({ code: 'source_is_also_master', address: addr });
  }

  // duplicate source -> different masters
  const bySource = new Map<string, Set<string>>();
  for (const e of entries) {
    if (!bySource.has(e.source)) bySource.set(e.source, new Set());
    bySource.get(e.source)?.add(e.master);
  }
  for (const [source, ms] of bySource) {
    if (ms.size > 1) errors.push({ code: 'duplicate_source', source, masters: [...ms] });
  }

  return errors;
}

function analyzeMapping(raw: string): MappingAnalysis {
  const { entries, invalid } = parseMapping(raw);
  return { entries, invalid, errors: validateMapping(entries) };
}

function fmtValidationError(e: ValidationError): string {
  switch (e.code) {
    case 'self_map':
      return `Self-map: ${e.source} points to itself`;
    case 'source_is_also_master':
      return `Chain: ${e.address} is both a source and a master`;
    case 'duplicate_source':
      return `Duplicate source: ${e.source} maps to multiple masters (${e.masters.join(', ')})`;
  }
}

function countEmails(text: string): { valid: number; invalid: number } {
  const tokens = text.split(/[\s,;]+/).map((t) => t.trim()).filter(Boolean);
  const valid = new Set(tokens.map((t) => t.toLowerCase()).filter((t) => EMAIL_RE.test(t)));
  const invalid = tokens.filter((t) => !EMAIL_RE.test(t.toLowerCase())).length;
  return { valid: valid.size, invalid };
}

export function MasterInboxForwarding(): JSX.Element {
  // ── Form ──
  const [mappingText, setMappingText] = useState('');
  const [emailsText, setEmailsText] = useState('');
  const [starting, setStarting] = useState(false);
  const [createErrors, setCreateErrors] = useState<ValidationError[]>([]);

  // ── List / drilldown ──
  const [runs, setRuns] = useState<RunInfo[]>([]);
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);

  // ── Blocked re-check editors ──
  const [editMapping, setEditMapping] = useState('');
  const [editEmails, setEditEmails] = useState('');
  const [rechecking, setRechecking] = useState(false);
  const [recheckErrors, setRecheckErrors] = useState<ValidationError[]>([]);
  const seededRef = useRef<string | null>(null);

  // ── Action flags ──
  const [applying, setApplying] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [recoveringStuck, setRecoveringStuck] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [selectedConflicts, setSelectedConflicts] = useState<Set<string>>(new Set());

  const [toast, setToast] = useState<Toast | null>(null);
  const detailPollRef = useRef<number | null>(null);

  const showToast = useCallback((type: Toast['type'], message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 6000);
  }, []);

  const formAnalysis = useMemo(() => analyzeMapping(mappingText), [mappingText]);
  const editAnalysis = useMemo(() => analyzeMapping(editMapping), [editMapping]);
  const formEmails = useMemo(() => countEmails(emailsText), [emailsText]);
  const editEmailCount = useMemo(() => countEmails(editEmails), [editEmails]);

  // ── Recent runs list ──
  const refreshRuns = useCallback(async (): Promise<void> => {
    try {
      const r = await api<{ runs: RunInfo[] }>('/api/master-inbox-runs?limit=15');
      setRuns(r.runs);
    } catch {
      // transient - next poll retries
    }
  }, []);

  useEffect(() => {
    void refreshRuns();
  }, [refreshRuns]);

  // List poll: 3s while a run is applying, else 7s.
  useEffect(() => {
    const hasInflight = runs.some((r) => r.status === 'applying');
    const timer = window.setInterval(() => void refreshRuns(), hasInflight ? 3000 : 7000);
    return () => window.clearInterval(timer);
  }, [runs, refreshRuns]);

  // ── Drilldown ──
  const refreshDetail = useCallback(
    async (runId: string): Promise<void> => {
      try {
        const d = await api<Detail>(`/api/master-inbox-runs/${runId}`);
        setDetail(d);
      } catch (err) {
        showToast('error', err instanceof Error ? err.message : 'Failed to load run');
      }
    },
    [showToast],
  );

  // Detail poll: 2.5s until terminal.
  useEffect(() => {
    if (detailPollRef.current !== null) {
      window.clearInterval(detailPollRef.current);
      detailPollRef.current = null;
    }
    setRecheckErrors([]);
    setSelectedConflicts(new Set());
    seededRef.current = null;
    if (!openRunId) {
      setDetail(null);
      return;
    }
    void refreshDetail(openRunId);
    detailPollRef.current = window.setInterval(() => void refreshDetail(openRunId), 2500);
    return () => {
      if (detailPollRef.current !== null) window.clearInterval(detailPollRef.current);
    };
  }, [openRunId, refreshDetail]);

  useEffect(() => {
    if (
      detail &&
      (detail.run.status === 'completed' || detail.run.status === 'failed') &&
      detailPollRef.current !== null
    ) {
      window.clearInterval(detailPollRef.current);
      detailPollRef.current = null;
    }
  }, [detail]);

  // Seed the blocked-state editors once per run from its reconciled data.
  useEffect(() => {
    if (!detail || detail.run.status !== 'blocked') return;
    if (seededRef.current === detail.run.id) return;
    seededRef.current = detail.run.id;
    setEditMapping(
      detail.entries.map((e) => `${e.source_email},${e.master_email}`).join('\n'),
    );
    const uniquePanels = [...new Set(detail.panels.map((p) => p.panel_email))];
    setEditEmails(uniquePanels.join('\n'));
  }, [detail]);

  // ── Actions ──
  async function startRun(): Promise<void> {
    setStarting(true);
    setCreateErrors([]);
    try {
      const body: Record<string, unknown> = {
        mapping_text: mappingText,
        emails_text: emailsText,
      };
      const r = await api<CreateResp>('/api/master-inbox-runs', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (r.invalid.length > 0) {
        showToast('info', `${r.invalid.length} mapping line(s) ignored as invalid`);
      }
      if (r.run_id === null) {
        // Blocked pre-reconcile by loop validation - surface inline, do not navigate.
        setCreateErrors(r.validation_errors ?? []);
        showToast('error', 'Mapping has loop violations - fix and retry');
      } else {
        setMappingText('');
        setEmailsText('');
        await refreshRuns();
        setOpenRunId(r.run_id);
      }
    } catch (err) {
      const e = err as ApiError;
      const ve = (e.data as { validation_errors?: ValidationError[] } | undefined)
        ?.validation_errors;
      if (ve && ve.length > 0) setCreateErrors(ve);
      showToast('error', e.message);
    } finally {
      setStarting(false);
    }
  }

  async function recheck(runId: string): Promise<void> {
    setRechecking(true);
    setRecheckErrors([]);
    // Client-side loop preview first; server re-validates authoritatively.
    if (editAnalysis.errors.length > 0) {
      setRecheckErrors(editAnalysis.errors);
      setRechecking(false);
      showToast('error', 'Mapping still has loop violations - fix and retry');
      return;
    }
    try {
      const r = await api<CreateResp>(`/api/master-inbox-runs/${runId}/recheck`, {
        method: 'POST',
        body: JSON.stringify({ mapping_text: editMapping, emails_text: editEmails }),
      });
      if (r.invalid.length > 0) {
        showToast('info', `${r.invalid.length} mapping line(s) ignored as invalid`);
      }
      if (r.validation_errors && r.validation_errors.length > 0) {
        setRecheckErrors(r.validation_errors);
        showToast('error', 'Server flagged loop violations');
      } else {
        showToast('success', 'Re-checked');
      }
      await refreshDetail(runId);
      await refreshRuns();
    } catch (err) {
      const e = err as ApiError;
      const ve = (e.data as { validation_errors?: ValidationError[] } | undefined)
        ?.validation_errors;
      if (ve && ve.length > 0) setRecheckErrors(ve);
      showToast('error', e.message);
    } finally {
      setRechecking(false);
    }
  }

  async function applyRun(runId: string): Promise<void> {
    setApplying(true);
    try {
      const r = await api<{ enqueued: number }>(`/api/master-inbox-runs/${runId}/apply`, {
        method: 'POST',
      });
      showToast('success', `Apply enqueued - ${r.enqueued} panel(s)`);
      await refreshDetail(runId);
      await refreshRuns();
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Apply failed');
    } finally {
      setApplying(false);
    }
  }

  async function retryErrors(runId: string): Promise<void> {
    setRetrying(true);
    try {
      const r = await api<{ retried: number }>(
        `/api/master-inbox-runs/${runId}/retry-errors`,
        { method: 'POST' },
      );
      showToast(
        r.retried > 0 ? 'success' : 'info',
        r.retried > 0 ? `Re-enqueued ${r.retried} panel(s)` : 'No errored panels to retry',
      );
      await refreshDetail(runId);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Retry failed');
    } finally {
      setRetrying(false);
    }
  }

  async function recoverStuck(runId: string): Promise<void> {
    setRecoveringStuck(true);
    try {
      const r = await api<{ recovered: number }>(
        `/api/master-inbox-runs/${runId}/recover-stuck`,
        { method: 'POST' },
      );
      showToast(
        r.recovered > 0 ? 'success' : 'info',
        r.recovered > 0
          ? `Re-enqueued ${r.recovered} stuck panel(s)`
          : 'No stuck panels to recover',
      );
      await refreshDetail(runId);
      await refreshRuns();
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Recover failed');
    } finally {
      setRecoveringStuck(false);
    }
  }

  async function toggleAutoRetry(runId: string, enabled: boolean): Promise<void> {
    try {
      await api<{ enabled: boolean; attemptsUsed: number; attemptsCap: number }>(
        `/api/master-inbox-runs/${runId}/auto-retry`,
        { method: 'POST', body: JSON.stringify({ enabled }) },
      );
      await refreshDetail(runId);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Toggle failed');
    }
  }

  async function resolveConflicts(
    runId: string,
    mode: 'override' | 'skip',
    sources: string[],
  ): Promise<void> {
    setResolving(true);
    try {
      const r = await api<{ resolved: 'override' | 'skip'; requeued: number }>(
        `/api/master-inbox-runs/${runId}/resolve-conflicts`,
        { method: 'POST', body: JSON.stringify({ mode, entries: sources }) },
      );
      showToast('success', `Applied ${r.resolved} to conflicts, re-queued ${r.requeued}`);
      setSelectedConflicts(new Set());
      await refreshDetail(runId);
      await refreshRuns();
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Resolve failed');
    } finally {
      setResolving(false);
    }
  }

  function toggleConflict(source: string): void {
    setSelectedConflicts((prev) => {
      const next = new Set(prev);
      if (next.has(source)) next.delete(source);
      else next.add(source);
      return next;
    });
  }

  // ═══════════════════════════ Drilldown ═══════════════════════════
  if (openRunId && detail) {
    const { run, panels, entries } = detail;
    const terminal = run.status === 'completed' || run.status === 'failed';
    const conflictEntries = entries.filter((e) => e.apply_result === 'conflict');
    const conflictSources = conflictEntries.map((e) => e.source_email);
    const addedCount = entries.filter((e) => e.apply_result === 'added').length;
    const alreadyCount = entries.filter((e) => e.apply_result === 'already').length;
    const verifiedCount = entries.filter((e) => e.verified).length;

    // Group entries by target panel for the reconciled preview.
    const byPanel = new Map<string, EntryRow[]>();
    for (const e of entries) {
      if (e.match_status !== 'matched') continue;
      const key = e.target_panel_email || '(unassigned)';
      if (!byPanel.has(key)) byPanel.set(key, []);
      byPanel.get(key)?.push(e);
    }
    const previewPanels = [...new Set(panels.map((p) => p.panel_email))].sort();

    const unmatched = entries.filter((e) => e.match_status === 'unmatched');
    const unreachable = panels.filter((p) => p.reconcile_status === 'unreachable');

    return (
      <div className="mif-root">
        <div className="mif-scroll">
          <div className="mif-runheader">
            <button
              type="button"
              className="mif-btn mif-btn--ghost mif-btn--small"
              onClick={() => setOpenRunId(null)}
            >
              ← Back
            </button>
            <h1 className="mif-title">Master Inbox run</h1>
            <span className={`mif-status mif-status--${runStatusView(run.status, run.panels_errored).cls}`}>
              {runStatusView(run.status, run.panels_errored).label}
            </span>
          </div>

          {toast && <div className={`mif-toast mif-toast--${toast.type}`}>{toast.message}</div>}

          <div className="mif-runmeta">
            <div>
              <strong>Rule</strong>
              {run.rule_name ?? '-'}
            </div>
            <div>
              <strong>Route to original</strong>
              {run.route_to_original ? 'yes' : 'no'}
            </div>
            <div>
              <strong>Created</strong>
              {fmtDate(run.created_at)}
            </div>
            <div>
              <strong>Triggered by</strong>
              {run.triggered_by ?? '-'}
            </div>
          </div>

          {/* ── blocked: incongruency worklist ── */}
          {run.status === 'blocked' && (
            <>
              <section className="mif-card">
                <div className="mif-card-header">
                  <h2 className="mif-card-title">Incongruencies to resolve</h2>
                </div>
                <div className="mif-card-body">
                  <div className="mif-worklist">
                    {unmatched.length === 0 &&
                      unreachable.length === 0 &&
                      recheckErrors.length === 0 && (
                        <p className="mif-cell-faint">
                          No open incongruencies - re-check to advance the run.
                        </p>
                      )}

                    {recheckErrors.length > 0 && (
                      <div className="mif-worklist-group">
                        <div className="mif-worklist-grouphead">
                          Loop violations
                          <span className="mif-worklist-count">{recheckErrors.length}</span>
                        </div>
                        {recheckErrors.map((e, i) => (
                          <div className="mif-worklist-item" key={`ve-${i}`}>
                            <span className="mif-worklist-item-reason">
                              {fmtValidationError(e)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {unmatched.length > 0 && (
                      <div className="mif-worklist-group">
                        <div className="mif-worklist-grouphead">
                          Unmatched entries
                          <span className="mif-worklist-count">{unmatched.length}</span>
                        </div>
                        {unmatched.map((e) => (
                          <div className="mif-worklist-item" key={`um-${e.source_email}`}>
                            <span className="mif-worklist-item-main">{e.source_email}</span>
                            <span className="mif-worklist-item-reason">
                              {e.unmatched_reason ?? 'No matching panel found'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {unreachable.length > 0 && (
                      <div className="mif-worklist-group">
                        <div className="mif-worklist-grouphead">
                          Unreachable panels
                          <span className="mif-worklist-count">{unreachable.length}</span>
                        </div>
                        {unreachable.map((p) => (
                          <div className="mif-worklist-item" key={`ur-${p.panel_email}`}>
                            <span className="mif-worklist-item-main">{p.panel_email}</span>
                            <span className="mif-worklist-item-reason">
                              {p.error_message ?? p.error_code ?? 'Panel could not be reached'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </section>

              <section className="mif-card">
                <div className="mif-card-header">
                  <h2 className="mif-card-title">Edit and re-check</h2>
                </div>
                <div className="mif-card-body">
                  {editAnalysis.errors.length > 0 && (
                    <div className="mif-warn mif-warn--hard">
                      <p className="mif-warn-title">Loop violations block re-check</p>
                      <ul className="mif-warn-list">
                        {editAnalysis.errors.map((e, i) => (
                          <li key={i}>{fmtValidationError(e)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="mif-edit-grid">
                    <label className="mif-field">
                      <span className="mif-field-label">
                        Mapping (source,master per line)
                        {editAnalysis.entries.length > 0 &&
                          ` - ${editAnalysis.entries.length} valid`}
                        {editAnalysis.invalid.length > 0 &&
                          ` (${editAnalysis.invalid.length} invalid)`}
                      </span>
                      <textarea
                        className="mif-textarea mif-mono"
                        rows={10}
                        value={editMapping}
                        onChange={(e) => setEditMapping(e.target.value)}
                        spellCheck={false}
                      />
                    </label>
                    <label className="mif-field">
                      <span className="mif-field-label">
                        Panel emails
                        {editEmailCount.valid > 0 && ` - ${editEmailCount.valid} parsed`}
                        {editEmailCount.invalid > 0 &&
                          ` (${editEmailCount.invalid} invalid ignored)`}
                      </span>
                      <textarea
                        className="mif-textarea mif-mono"
                        rows={10}
                        value={editEmails}
                        onChange={(e) => setEditEmails(e.target.value)}
                        spellCheck={false}
                      />
                    </label>
                  </div>
                  <div className="mif-actions-row">
                    <div className="mif-actions-spacer" />
                    <button
                      type="button"
                      className="mif-btn mif-btn--primary"
                      onClick={() => void recheck(run.id)}
                      disabled={
                        rechecking ||
                        editAnalysis.errors.length > 0 ||
                        editAnalysis.entries.length === 0
                      }
                    >
                      {rechecking ? 'Re-checking…' : 'Re-check'}
                    </button>
                  </div>
                </div>
              </section>
            </>
          )}

          {/* ── reconciled: clean bill + per-panel preview ── */}
          {run.status === 'reconciled' && (
            <>
              <div className="mif-banner mif-banner--clean">
                Clean bill - every entry matched a reachable panel. Review the preview, then apply.
              </div>
              <div className="mif-runactions">
                <div className="mif-actions-spacer" />
                <button
                  type="button"
                  className="mif-btn mif-btn--primary"
                  onClick={() => void applyRun(run.id)}
                  disabled={applying}
                >
                  {applying ? 'Applying…' : 'Apply to panels'}
                </button>
              </div>
              <section className="mif-card">
                <div className="mif-card-header">
                  <h2 className="mif-card-title">Per-panel preview</h2>
                </div>
                <div className="mif-card-body">
                  <div className="mif-preview">
                    {previewPanels.map((panelEmail) => {
                      const group = byPanel.get(panelEmail) ?? [];
                      const willSkip = group.length === 0;
                      return (
                        <div
                          key={panelEmail}
                          className={`mif-preview-group${willSkip ? ' mif-preview-group--skip' : ''}`}
                        >
                          <div className="mif-preview-header">
                            <span className="mif-preview-panel">{panelEmail}</span>
                            {willSkip ? (
                              <span className="mif-status mif-status--skipped">will skip</span>
                            ) : (
                              <span className="mif-summary-pill mif-summary-pill--ok">
                                {group.length} forward{group.length === 1 ? '' : 's'}
                              </span>
                            )}
                          </div>
                          {!willSkip && (
                            <div className="mif-preview-body">
                              {group.map((e) => (
                                <div
                                  className="mif-preview-line"
                                  key={`${panelEmail}-${e.source_email}`}
                                >
                                  <span>{e.source_email}</span>
                                  <span className="mif-preview-arrow">→</span>
                                  <span className="mif-preview-master">{e.master_email}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>
            </>
          )}

          {/* ── applying / completed / failed: results ── */}
          {(run.status === 'applying' ||
            run.status === 'completed' ||
            run.status === 'failed') && (
            <>
              <div className="mif-runsummary">
                <span className="mif-summary-pill">{entries.length} entries</span>
                <span className="mif-summary-pill mif-summary-pill--ok">{addedCount} added</span>
                <span className="mif-summary-pill mif-summary-pill--already">
                  {alreadyCount} already
                </span>
                <span className="mif-summary-pill mif-summary-pill--conflict">
                  {conflictEntries.length} conflict
                </span>
                <span className="mif-summary-pill mif-summary-pill--err">
                  {run.panels_errored} errored
                </span>
                <span className="mif-summary-pill mif-summary-pill--verified">
                  {verifiedCount} verified
                </span>
              </div>

              <div className="mif-runactions">
                <label className="mif-filter">
                  <input
                    type="checkbox"
                    checked={run.auto_retry_enabled}
                    onChange={(e) => void toggleAutoRetry(run.id, e.target.checked)}
                  />
                  Auto-retry errored ({run.auto_retry_attempts_used}/
                  {run.auto_retry_attempts_cap} used)
                </label>
                <div className="mif-actions-spacer" />
                {run.status === 'applying' && (
                  <button
                    type="button"
                    className="mif-btn mif-btn--secondary mif-btn--small"
                    onClick={() => void recoverStuck(run.id)}
                    disabled={recoveringStuck}
                    title="Re-enqueue panels stuck pending, or running for 15+ minutes, on this applying run"
                  >
                    {recoveringStuck ? 'Recovering…' : 'Recover stuck'}
                  </button>
                )}
                <button
                  type="button"
                  className="mif-btn mif-btn--primary mif-btn--small"
                  onClick={() => void retryErrors(run.id)}
                  disabled={retrying || !terminal || run.panels_errored === 0}
                  title={
                    terminal
                      ? 'Reset errored panels and re-enqueue them'
                      : 'Available once the run finishes'
                  }
                >
                  {retrying ? 'Retrying…' : `Retry errors (${run.panels_errored})`}
                </button>
              </div>

              {/* Conflict resolution */}
              {conflictEntries.length > 0 && (
                <section className="mif-card">
                  <div className="mif-card-header">
                    <h2 className="mif-card-title">
                      Conflicts ({conflictEntries.length})
                    </h2>
                  </div>
                  <div className="mif-conflict-toolbar">
                    <button
                      type="button"
                      className="mif-btn mif-btn--ghost mif-btn--small"
                      onClick={() => setSelectedConflicts(new Set(conflictSources))}
                      disabled={resolving}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="mif-btn mif-btn--ghost mif-btn--small"
                      onClick={() => setSelectedConflicts(new Set())}
                      disabled={resolving}
                    >
                      Select none
                    </button>
                    <div className="mif-actions-spacer" />
                    <button
                      type="button"
                      className="mif-btn mif-btn--secondary mif-btn--small"
                      onClick={() =>
                        void resolveConflicts(run.id, 'skip', [...selectedConflicts])
                      }
                      disabled={resolving || selectedConflicts.size === 0}
                    >
                      Skip ({selectedConflicts.size})
                    </button>
                    <button
                      type="button"
                      className="mif-btn mif-btn--primary mif-btn--small"
                      onClick={() =>
                        void resolveConflicts(run.id, 'override', [...selectedConflicts])
                      }
                      disabled={resolving || selectedConflicts.size === 0}
                    >
                      Override ({selectedConflicts.size})
                    </button>
                  </div>
                  <div className="mif-table-wrap">
                    <table className="mif-table">
                      <thead>
                        <tr>
                          <th className="mif-col-center">
                            <input
                              type="checkbox"
                              className="mif-check"
                              checked={
                                selectedConflicts.size === conflictSources.length &&
                                conflictSources.length > 0
                              }
                              onChange={(e) =>
                                setSelectedConflicts(
                                  e.target.checked
                                    ? new Set(conflictSources)
                                    : new Set(),
                                )
                              }
                            />
                          </th>
                          <th>Source</th>
                          <th>Requested master</th>
                          <th>Existing master</th>
                          <th>Target panel</th>
                        </tr>
                      </thead>
                      <tbody>
                        {conflictEntries.map((e) => (
                          <tr key={`cf-${e.source_email}`}>
                            <td className="mif-col-center">
                              <input
                                type="checkbox"
                                className="mif-check"
                                checked={selectedConflicts.has(e.source_email)}
                                onChange={() => toggleConflict(e.source_email)}
                              />
                            </td>
                            <td className="mif-cell-mono">{e.source_email}</td>
                            <td className="mif-cell-mono">{e.master_email}</td>
                            <td className="mif-cell-mono">{e.existing_master ?? '-'}</td>
                            <td className="mif-cell-mono">{e.target_panel_email}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {/* Per-panel results */}
              <section className="mif-card">
                <div className="mif-card-header">
                  <h2 className="mif-card-title">Panels</h2>
                </div>
                <div className="mif-table-wrap">
                  <table className="mif-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Source</th>
                        <th className="mif-col-center">Status</th>
                        <th className="mif-col-num">Added</th>
                        <th className="mif-col-num">Already</th>
                        <th className="mif-col-num">Conflict</th>
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {panels.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="mif-empty">
                            No panels.
                          </td>
                        </tr>
                      ) : (
                        panels.map((p) => (
                          <tr key={p.panel_email}>
                            <td className="mif-cell-mono">{p.panel_email}</td>
                            <td>{SOURCE_LABEL[p.source]}</td>
                            <td className="mif-col-center">
                              <span className={`mif-status mif-status--${p.apply_status}`}>
                                {p.apply_status}
                              </span>
                            </td>
                            <td className="mif-col-num">{p.added_count}</td>
                            <td className="mif-col-num">{p.already_count}</td>
                            <td className="mif-col-num">{p.conflict_count}</td>
                            <td className="mif-cell-error">
                              {p.error_message
                                ? `${p.error_code ?? ''} ${p.error_message}`
                                : '-'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Per-entry results */}
              <section className="mif-card">
                <div className="mif-card-header">
                  <h2 className="mif-card-title">Entries</h2>
                </div>
                <div className="mif-table-wrap">
                  <table className="mif-table">
                    <thead>
                      <tr>
                        <th>Source</th>
                        <th>Master</th>
                        <th>Target panel</th>
                        <th className="mif-col-center">Result</th>
                        <th className="mif-col-center">Verified</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="mif-empty">
                            No entries.
                          </td>
                        </tr>
                      ) : (
                        entries.map((e) => (
                          <tr key={`en-${e.source_email}`}>
                            <td className="mif-cell-mono">{e.source_email}</td>
                            <td className="mif-cell-mono">{e.master_email}</td>
                            <td className="mif-cell-mono">{e.target_panel_email || '-'}</td>
                            <td className="mif-col-center">
                              <span className={`mif-status mif-status--${e.apply_result}`}>
                                {e.apply_result}
                              </span>
                            </td>
                            <td className="mif-col-center">{e.verified ? '✓' : '-'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    );
  }

  // ═══════════════════════════ Form + list ═══════════════════════════
  const startDisabled =
    starting ||
    formAnalysis.entries.length === 0 ||
    formAnalysis.errors.length > 0 ||
    formEmails.valid === 0;

  return (
    <div className="mif-root">
      <div className="mif-scroll">
        <header className="mif-header">
          <h1 className="mif-title">Master Inbox Forwarding</h1>
          <p className="mif-subtitle">
            Paste a forwarding mapping (<strong>source,master</strong> per line) and the panel
            emails that own those mailboxes. The run reconciles first - matching each source to a
            reachable panel and loop-checking the mapping - then, once you approve, applies the
            forwarding across the panels. Conflicts (an existing different master) are surfaced
            for per-entry override or skip.
          </p>
        </header>

        {toast && <div className={`mif-toast mif-toast--${toast.type}`}>{toast.message}</div>}

        <section className="mif-card">
          <div className="mif-card-header">
            <h2 className="mif-card-title">Start a run</h2>
          </div>
          <div className="mif-card-body">
            {createErrors.length > 0 && (
              <div className="mif-warn mif-warn--hard">
                <p className="mif-warn-title">Server blocked this mapping (loop violations)</p>
                <ul className="mif-warn-list">
                  {createErrors.map((e, i) => (
                    <li key={i}>{fmtValidationError(e)}</li>
                  ))}
                </ul>
              </div>
            )}

            <label className="mif-field">
              <span className="mif-field-label">
                Mapping (source,master per line)
                {formAnalysis.entries.length > 0 && ` - ${formAnalysis.entries.length} valid`}
                {formAnalysis.invalid.length > 0 &&
                  ` (${formAnalysis.invalid.length} invalid ignored)`}
              </span>
              <textarea
                className="mif-textarea mif-mono"
                rows={8}
                placeholder={'user@domain.com,master@inbox.com\nother@domain.com,master@inbox.com'}
                value={mappingText}
                onChange={(e) => setMappingText(e.target.value)}
                spellCheck={false}
              />
            </label>

            {formAnalysis.errors.length > 0 && (
              <div className="mif-warn">
                <p className="mif-warn-title">
                  Loop violations - fix these before starting
                </p>
                <ul className="mif-warn-list">
                  {formAnalysis.errors.map((e, i) => (
                    <li key={i}>{fmtValidationError(e)}</li>
                  ))}
                </ul>
              </div>
            )}

            <label className="mif-field">
              <span className="mif-field-label">
                Panel emails
                {formEmails.valid > 0 && ` - ${formEmails.valid} parsed`}
                {formEmails.invalid > 0 && ` (${formEmails.invalid} invalid ignored)`}
              </span>
              <textarea
                className="mif-textarea mif-mono"
                rows={6}
                placeholder={'admin@domain1.com\nadmin@domain2.com'}
                value={emailsText}
                onChange={(e) => setEmailsText(e.target.value)}
                spellCheck={false}
              />
            </label>

            <div className="mif-actions-row">
              <div className="mif-actions-spacer" />
              <button
                type="button"
                className="mif-btn mif-btn--primary"
                onClick={() => void startRun()}
                disabled={startDisabled}
              >
                {starting
                  ? 'Starting…'
                  : `Start run (${formAnalysis.entries.length} mapping${
                      formAnalysis.entries.length === 1 ? '' : 's'
                    })`}
              </button>
            </div>
          </div>
        </section>

        <section className="mif-card">
          <div className="mif-card-header">
            <h2 className="mif-card-title">Recent runs</h2>
          </div>
          <div className="mif-table-wrap">
            <table className="mif-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Rule</th>
                  <th className="mif-col-center">Status</th>
                  <th className="mif-col-num">Total</th>
                  <th className="mif-col-num">Done</th>
                  <th className="mif-col-num">Errored</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {runs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="mif-empty">
                      No runs yet.
                    </td>
                  </tr>
                ) : (
                  runs.map((r) => (
                    <tr
                      key={r.id}
                      className={`mif-row--clickable${
                        (r.status === 'completed' || r.status === 'failed') && r.panels_errored > 0
                          ? ' mif-row--errored'
                          : ''
                      }`}
                      onClick={() => setOpenRunId(r.id)}
                    >
                      <td className="mif-cell-mono">{fmtDate(r.created_at)}</td>
                      <td className="mif-cell-clip" title={r.rule_name ?? ''}>
                        {r.rule_name ?? '-'}
                      </td>
                      <td className="mif-col-center">
                        <span className={`mif-status mif-status--${runStatusView(r.status, r.panels_errored).cls}`}>
                          {runStatusView(r.status, r.panels_errored).label}
                        </span>
                      </td>
                      <td className="mif-col-num">{r.panels_total}</td>
                      <td className="mif-col-num">{r.panels_done}</td>
                      <td className="mif-col-num">{r.panels_errored}</td>
                      <td className="mif-cell-faint">{r.triggered_by ?? '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
